import type {
  IAiGeneratedAttachment,
  IAiPageContext,
  IChatAttachment,
} from "@/types/(my-space)/ai-assistant.interface";

export type MayaMessageRole = "user" | "bot";

/** One progress line the AI streamed while working on a reply. */
export interface IMayaStep {
  message: string;
  /** Local time it arrived, already formatted ("hh:mm a"). */
  at: string;
}

export type MayaMessageStatus = "sent" | "pending" | "failed";

export interface IMayaMessage {
  id: string;
  role: MayaMessageRole;
  text: string;
  /** When set, `text` is an i18n key resolved at render (locale-reactive copy
   *  such as the initial greeting). Absent for API/user text, which is literal. */
  i18nKey?: boolean;
  ts: number;
  status?: MayaMessageStatus;
  /** The stored message id, once the server has saved a turn sent here —
   *  numeric for saved chats, a hex string for temporary ones. */
  serverId?: number | string;
  /** Files the user sent with the turn (saved chats opened from the list). */
  attachments?: IChatAttachment[];
  /** Files the AI produced for its reply (e.g. a CSV export). */
  generatedAttachments?: IAiGeneratedAttachment[];
  /** Why a file the AI tried to produce couldn't be prepared. */
  attachmentErrors?: string[];
  /** What the AI did on the way to this reply ("Steps" popover). */
  steps?: IMayaStep[];
}

/** True once the chat holds anything besides the greeting (which is an i18n
 *  key shown to new chats, never stored). */
export const hasRealMessages = (messages: IMayaMessage[]): boolean =>
  messages.some((m) => !m.i18nKey);

export interface IMayaError {
  message: string;
  failedUserMessageId?: string;
}

export interface IMayaPageContext {
  /** Localized page title, shown in the sidebar's context lens. */
  label: string;
  /** Localized breadcrumb trail above the title, outermost first. */
  trail: string[];
  /** What the chat request carries as `pageContext`. */
  payload: IAiPageContext;
}
